import { create } from 'zustand'
import { api } from '@/api/client'
import type { Activity, CalView, Space } from '@/types/models'
import { useSpaceStore } from './spaceStore'
import { useAuthStore } from './authStore'

interface CalendarState {
  curMonth: Date
  calView: CalView
  weekOffset: number
  monthActs: Record<string, Activity[]>
  loadingMonth: boolean
  error: string | null
  loadMonth: () => Promise<void>
  navigateMonth: (delta: number) => void
  switchView: (view: CalView) => void
  navigateWeek: (delta: number) => void
}

export const useCalendarStore = create<CalendarState>()((set, get) => ({
  curMonth: new Date(),
  calView: 'month',
  weekOffset: 0,
  monthActs: {},
  loadingMonth: false,
  error: null,

  loadMonth: async () => {
    const { curMonth, loadingMonth } = get()
    if (loadingMonth) return
    set({ loadingMonth: true })
    const { activeSpace, spaces } = useSpaceStore.getState()
    const { userInfo } = useAuthStore.getState()
    const y = curMonth.getFullYear()
    const m = String(curMonth.getMonth() + 1).padStart(2, '0')

    let url = ''
    if (activeSpace === 'default') {
      url = `/api/activities/${y}-${m}`
    } else if (activeSpace.startsWith('space:')) {
      const sid = activeSpace.replace('space:', '')
      url = `/api/spaces/${sid}/activities/${y}-${m}`
    }

    if (!url) { set({ loadingMonth: false }); return }

    const acts: Record<string, Activity[]> = {}
    try {
      const activities = await api.get<Activity[]>(url)
      ;(Array.isArray(activities) ? activities : []).forEach(a => {
      if (!acts[a.date]) acts[a.date] = []
      acts[a.date].push(a)
      })
    } catch (e) {
      set({ error: e instanceof Error ? e.message : '加载失败', loadingMonth: false })
      return
    }

    if (activeSpace === 'default' && userInfo) {
      const viewSpaces = Array.isArray(userInfo.defaultViewSpaces) ? userInfo.defaultViewSpaces : []
      const fetchPromises: Promise<{ sd?: Activity[]; sp?: Space; vd?: Activity[] } | null>[] = viewSpaces
        .filter(sid => spaces.find(s => s.id === sid))
        .map(sid => {
          const sp = spaces.find(s => s.id === sid)!
          return api.get<Activity[]>(`/api/spaces/${sid}/activities/${y}-${m}`)
            .then(sd => ({ sd, sp }))
            .catch(() => null)
        })

      if (userInfo.vaultVisible && api.vaultToken) {
        fetchPromises.push(
          api.get<Activity[]>(`/api/vault/activities/${y}-${m}`)
            .then(vd => ({ vd }))
            .catch(() => null)
        )
      }

      // 并行加载所有空间数据
      try {
        const results = await Promise.allSettled(fetchPromises)
        for (const result of results) {
          if (result.status !== 'fulfilled' || !result.value) continue
          const val = result.value
          if ('sp' in val && val.sp) {
            const { sd, sp } = val
            if (Array.isArray(sd)) {
              sd.forEach(a => {
                a._source = sp.name
                a._sourceIcon = sp.type === 'couple' ? '💕' : '👥'
                a._readonly = true
                if (!acts[a.date]) acts[a.date] = []
                acts[a.date].push(a)
              })
            }
          } else if ('vd' in val) {
            const { vd } = val
            if (Array.isArray(vd)) {
              vd.forEach(a => {
                a._source = '🔒 私密'
                a._sourceIcon = ''
                a._readonly = true
                if (!acts[a.date]) acts[a.date] = []
                acts[a.date].push(a)
              })
            }
          }
        }
      } catch {
        // Space data fetch failed, continue with what we have
      }
    }

    set({ monthActs: acts, loadingMonth: false })
  },

  navigateMonth: (delta) => {
    const { curMonth } = get()
    const next = new Date(curMonth)
    next.setMonth(next.getMonth() + delta)
    set({ curMonth: next, weekOffset: 0, error: null })
  },

  switchView: (view) => set({ calView: view, weekOffset: 0, error: null }),

  navigateWeek: (delta) => {
    set(s => ({ weekOffset: s.weekOffset + delta }))
  },
}))
